import React, { useEffect, useState } from "react"
import { useSearchParam } from "react-use"
import { useHMSActions } from "@100mslive/react-sdk"
import styled from "styled-components"
import Loader from "components/loader"
import PreviewJoin from "./preview/preview-join"
import { useWebinarAppContext } from "../state"
import { useTokenEndpoint } from "./app-data/use-uisettings"
import {
  QUERY_PARAM_AUTH_TOKEN,
  QUERY_PARAM_NAME,
  QUERY_PARAM_PREVIEW_AS_ROLE,
  QUERY_PARAM_SKIP_PREVIEW,
} from "../common/constants"

const convertPreviewError = (error) => {
  if (error.action === "GET_TOKEN" && error.code === 403) {
    return {
      title: "Room code is disabled",
      body: ErrorWithSupportLink("Room code corresponding to this link is no more active."),
    }
  }
  if (error.action === "GET_TOKEN" && error.code === 404) {
    return {
      title: "Room code does not exist",
      body: ErrorWithSupportLink("We could not find a room code corresponding to this link."),
    }
  }
  if (error.action === "GET_TOKEN" && error.code === 2003) {
    return {
      title: "Endpoint is not reachable",
      body: ErrorWithSupportLink(`Endpoint is not reachable. ${error.description}.`),
    }
  }
  if (error.response && error.response.status === 404) {
    return {
      title: "Room does not exist",
      body: ErrorWithSupportLink("We could not find a room corresponding to this link."),
    }
  }
  if (error.response && error.response.status === 403) {
    return {
      title: "Accessing room using this link format is disabled",
      body: ErrorWithSupportLink("You can re-enable this from the developer section in Dashboard."),
    }
  }
  return {
    title: "Error fetching token",
    body: ErrorWithSupportLink(
      "An error occurred while fetching the app token. Please look into logs for more details.",
    ),
  }
}

/**
 * query params exposed -
 * skip_preview=true => used by recording and streaming service, skips preview and directly joins
 *                      header and footer don't show up in this case
 * skip_preview_headful=true => used by automation testing to skip preview without impacting the UI
 * name=abc => gives the initial name for the peer joining
 * auth_token=123 => uses the passed in token to join instead of fetching from token endpoint
 * ui_mode=activespeaker => lands in active speaker mode after joining the room
 */

const PreviewScreen = React.memo(({ authTokenByRoomCodeEndpoint }) => {
  const hmsActions = useHMSActions()
  const tokenEndpoint = useTokenEndpoint()
  const [token, setToken] = useState(null)
  const [error, setError] = useState({ title: "", body: "" })
  const beamInToken = useSearchParam("token") === "beam_recording"
  let skipPreview = useSearchParam(QUERY_PARAM_SKIP_PREVIEW) === "true"
  skipPreview = skipPreview || beamInToken
  const initialName = useSearchParam(QUERY_PARAM_NAME) || (skipPreview ? "Beam" : "")
  const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE)
  const authToken = useSearchParam(QUERY_PARAM_AUTH_TOKEN)

  const { goToMeeting, role, roomId, title, trackingId } = useWebinarAppContext()

  useEffect(() => {
    if (authToken) {
      setToken(authToken)
      return
    }
    if (!tokenEndpoint || !roomId) {
      return
    }
    const roomCode = !role && roomId

    const getTokenFn = () => hmsActions.getAuthTokenByRoomCode({ roomCode })

    getTokenFn()
      .then((t) => setToken(t))
      .catch((err) => setError(convertPreviewError(err)))
  }, [hmsActions, tokenEndpoint, roomId, role, authToken, authTokenByRoomCodeEndpoint, setError])

  const onJoin = () => {
    goToMeeting()
  }

  return (
    <div
      style={{
        position: "relative",
        height: "75rem",
        overflow: "hidden",
      }}
    >
      <video
        width="560"
        height="560"
        autoPlay
        muted
        playsInline
        loop
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: "-1",
        }}
      >
        <source
          src="https://mindbodygreen-res.cloudinary.com/video/upload/w_1200,q_auto/coaching/webinar-background.mp4"
          type="video/mp4"
        />
      </video>
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          height: "100%",
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(5px)",
          zIndex: "0",
        }}
      />

      <div
        style={{
          position: "absolute",
          textAlign: "center",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: "black",
          zIndex: "1",
        }}
      >
        {token ? (
          <PreviewJoin
            initialName={initialName}
            skipPreview={skipPreview}
            onJoin={onJoin}
            token={token}
            asRole={previewAsRole}
            title={title}
            trackingId={trackingId}
          />
        ) : (
          <Loader width="50" height="50" />
        )}
      </div>
    </div>
  )
})

const Link = styled.a`
  color: "#2f80e1";
`

export const ErrorWithSupportLink = (errorMessage) => (
  <div>
    {errorMessage} If you think this is a mistake on our side, please create{" "}
    <Link target="_blank" href="https://github.com/100mslive/100ms-web/issues" rel="noreferrer">
      an issue
    </Link>{" "}
    or reach out over{" "}
    <Link target="_blank" href="https://discord.com/invite/kGdmszyzq2" rel="noreferrer">
      Discord
    </Link>
    .
  </div>
)

export default PreviewScreen
